// FR-AD5/F13b: legal hold CRUD — list (active + released), create, per-hold detail with items,
// add/remove item (grackle/user/team), and release. Every action here is audited server-side
// (legal_hold.rs's `audit::write` calls); this page just links to the audit log rather than
// repeating that record.
import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Badge, Card, Select } from "@grackle/ui";
import { SettingsSection } from "./SettingsSection";
import ConfirmDialog from "../components/ConfirmDialog";
import { api, ApiFail } from "../api/client";
import type {
  GrackleOut,
  LegalHoldDetailOut,
  LegalHoldOut,
  LegalHoldTargetKind,
  ListResponse,
  MemberOut,
  PagedResponse,
  TeamOut,
} from "../api/types";
import { Button, Field, Notice } from "../components/ui";
import type { AdminOutletContext } from "./AdminLayout";

export function targetKindLabel(kind: LegalHoldTargetKind): string {
  return kind === "grackle" ? "Recording" : kind === "user" ? "Person" : "Team";
}

/** Exported for `LegalHoldsPage.test.ts` (W7: "the release/add/remove flow calls the right
 * endpoints") — pulled out of the component so the exact paths can be asserted without
 * mounting it. Must match `legal_hold::router()`'s routes in `server/src/legal_hold.rs`. */
export function holdItemsEndpoint(orgId: string, holdId: string): string {
  return `/orgs/${orgId}/legal-holds/${holdId}/items`;
}
export function holdItemEndpoint(orgId: string, holdId: string, kind: LegalHoldTargetKind, targetId: string): string {
  return `/orgs/${orgId}/legal-holds/${holdId}/items/${kind}/${targetId}`;
}
export function holdReleaseEndpoint(orgId: string, holdId: string): string {
  return `/orgs/${orgId}/legal-holds/${holdId}/release`;
}

function HoldDetail({
  org,
  hold,
  members,
  teams,
  grackles,
  onReleased,
  onChanged,
}: {
  org: { id: string };
  hold: LegalHoldDetailOut;
  members: MemberOut[];
  teams: TeamOut[];
  grackles: GrackleOut[];
  onReleased: () => void;
  onChanged: (detail: LegalHoldDetailOut) => void;
}) {
  const [targetKind, setTargetKind] = useState<LegalHoldTargetKind>("grackle");
  const [targetId, setTargetId] = useState("");
  const [customGrackleId, setCustomGrackleId] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [releaseConfirm, setReleaseConfirm] = useState(false);
  const [releasing, setReleasing] = useState(false);

  async function onAddItem() {
    const chosenId = targetKind === "grackle" && customGrackleId.trim() ? customGrackleId.trim() : targetId;
    if (!chosenId) return;
    setAdding(true);
    setAddError(null);
    try {
      const detail = await api<LegalHoldDetailOut>(
        "POST",
        holdItemsEndpoint(org.id, hold.id),
        { target_kind: targetKind, target_id: chosenId },
      );
      onChanged(detail);
      setTargetId("");
      setCustomGrackleId("");
    } catch (err) {
      setAddError(err instanceof ApiFail ? err.message : "Couldn't add that to the hold.");
    } finally {
      setAdding(false);
    }
  }

  async function onRemoveItem(kind: LegalHoldTargetKind, id: string) {
    setAddError(null);
    try {
      const detail = await api<LegalHoldDetailOut>(
        "DELETE",
        holdItemEndpoint(org.id, hold.id, kind, id),
      );
      onChanged(detail);
    } catch (err) {
      setAddError(err instanceof ApiFail ? err.message : "Couldn't remove that item.");
    }
  }

  async function onReleaseConfirm() {
    setReleasing(true);
    try {
      await api("POST", holdReleaseEndpoint(org.id, hold.id));
      setReleaseConfirm(false);
      onReleased();
    } catch (err) {
      setAddError(err instanceof ApiFail ? err.message : "Couldn't release that hold.");
      setReleaseConfirm(false);
    } finally {
      setReleasing(false);
    }
  }

  const released = hold.released_at != null;

  return (
    <div className="admin-team-body">
      {hold.reason ? <div className="admin-row-sub">{hold.reason}</div> : null}

      {hold.items.length === 0 ? (
        <div className="admin-row-sub">Nothing on this hold yet.</div>
      ) : (
        <Card size="flush" className="admin-table-card">
<table className="admin-table">
          <thead>
            <tr>
              <th className="gk-overline">Kind</th>
              <th className="gk-overline">Target</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {hold.items.map((item) => (
              <tr key={`${item.target_kind}-${item.target_id}`}>
                <td>
                  <Badge size="sm">{targetKindLabel(item.target_kind)}</Badge>
                </td>
                <td>{item.label ?? item.target_id}</td>
                <td>
                  {!released ? (
                    <Button variant="secondary" size="sm"
                      onClick={() => void onRemoveItem(item.target_kind, item.target_id)}
                    >
                      Remove
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
</Card>
      )}

      {!released ? (
        <div className="admin-inline-form">
          <Select
            label="Add to hold"
            value={targetKind}
            onChange={(v) => {
              setTargetKind(v as LegalHoldTargetKind);
              setTargetId("");
              setCustomGrackleId("");
            }}
            options={[
              { value: "grackle", label: "Recording" },
              { value: "user", label: "Person" },
              { value: "team", label: "Team" },
            ]}
          />
          {targetKind === "user" ? (
            <Select
              label="Person"
              placeholder="Select a person…"
              value={targetId}
              onChange={setTargetId}
              options={members.map((m) => ({ value: m.user.id, label: m.user.name || m.user.email }))}
            />
          ) : targetKind === "team" ? (
            <Select
              label="Team"
              placeholder="Select a team…"
              value={targetId}
              onChange={setTargetId}
              options={teams.map((t) => ({ value: t.id, label: t.name }))}
            />
          ) : (
            <>
              <Select
                label="Recording"
                placeholder="Pick from recent recordings…"
                value={targetId}
                onChange={setTargetId}
                options={grackles.map((g) => ({ value: g.id, label: `${g.title || "Untitled"} · ${g.owner.name}` }))}
              />
              <Field
                label="…or enter a recording id"
                value={customGrackleId}
                onChange={(e) => setCustomGrackleId(e.target.value)}
                placeholder="grackle id"
              />
            </>
          )}
          <Button
            onClick={() => void onAddItem()}
            loading={adding}
            disabled={!targetId && !customGrackleId.trim()}
            style={{ marginTop: 22 }}
          >
            Add
          </Button>
        </div>
      ) : null}

      {addError ? <Notice tone="error">{addError}</Notice> : null}

      {!released ? (
        <div>
          <Button variant="danger" size="sm" onClick={() => setReleaseConfirm(true)}>
            Release hold
          </Button>
        </div>
      ) : (
        <div className="admin-row-sub">Released {new Date(hold.released_at as string).toLocaleString()}.</div>
      )}

      <ConfirmDialog
        open={releaseConfirm}
        title="Release legal hold"
        message="Release this hold? Every recording it covers becomes purgeable again (by the normal trash purge, retention policies, or manual delete) the instant this hold is released."
        confirmLabel="Release"
        danger
        loading={releasing}
        onClose={() => setReleaseConfirm(false)}
        onConfirm={onReleaseConfirm}
      />
    </div>
  );
}

export default function LegalHoldsPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const [holds, setHolds] = useState<LegalHoldOut[]>([]);
  const [members, setMembers] = useState<MemberOut[]>([]);
  const [teams, setTeams] = useState<TeamOut[]>([]);
  const [grackles, setGrackles] = useState<GrackleOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<LegalHoldDetailOut | null>(null);
  const [expandedError, setExpandedError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [holdsRes, membersRes, teamsRes, gracklesRes] = await Promise.all([
        api<ListResponse<LegalHoldOut>>("GET", `/orgs/${org.id}/legal-holds`),
        api<ListResponse<MemberOut>>("GET", `/orgs/${org.id}/members`),
        api<ListResponse<TeamOut>>("GET", `/orgs/${org.id}/teams`),
        api<PagedResponse<GrackleOut>>("GET", `/orgs/${org.id}/grackles?filter=all&limit=50`),
      ]);
      setHolds(holdsRes.items);
      setMembers(membersRes.items);
      setTeams(teamsRes.items);
      setGrackles(gracklesRes.items);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load legal holds.");
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onToggleExpand(hold: LegalHoldOut) {
    if (expanded?.id === hold.id) {
      setExpanded(null);
      return;
    }
    setExpandedError(null);
    try {
      const detail = await api<LegalHoldDetailOut>("GET", `/orgs/${org.id}/legal-holds/${hold.id}`);
      setExpanded(detail);
    } catch (err) {
      setExpandedError(err instanceof ApiFail ? err.message : "Couldn't load that hold.");
    }
  }

  async function onCreate() {
    if (!name.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      await api("POST", `/orgs/${org.id}/legal-holds`, { name: name.trim(), reason: reason.trim() });
      setName("");
      setReason("");
      await load();
    } catch (err) {
      setCreateError(err instanceof ApiFail ? err.message : "Couldn't create that hold.");
    } finally {
      setCreating(false);
    }
  }

  const active = holds.filter((h) => !h.released_at);
  const released = holds.filter((h) => h.released_at);

  function renderRow(hold: LegalHoldOut) {
    const isExpanded = expanded?.id === hold.id;
    return (
      <Card key={hold.id} className="admin-team-card">
        <button type="button" className="admin-team-head" aria-expanded={isExpanded} onClick={() => void onToggleExpand(hold)}>
          <span className="admin-team-name">{hold.name}</span>
          <Badge size="sm" tone={hold.released_at ? "neutral" : "accent"}>
            {hold.released_at ? "Released" : "Active"}
          </Badge>
          <span className="admin-team-count">
            {hold.released_at
              ? `Released ${new Date(hold.released_at).toLocaleDateString()}`
              : `Created ${new Date(hold.created_at).toLocaleDateString()}`}
          </span>
        </button>
        {isExpanded && expanded ? (
          <HoldDetail
            org={org}
            hold={expanded}
            members={members}
            teams={teams}
            grackles={grackles}
            onReleased={() => {
              setExpanded(null);
              void load();
            }}
            onChanged={(detail) => setExpanded(detail)}
          />
        ) : null}
      </Card>
    );
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Legal holds</h1>
          <p>
            A held recording, person, or team can't be permanently deleted — trashing still
            works, but every purge path is blocked until the hold is released. All hold actions
            are recorded in the <Link to={`/o/${org.slug}/admin/audit-log`}>audit log</Link>.
          </p>
        </div>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}
      {expandedError ? <Notice tone="error">{expandedError}</Notice> : null}

      <SettingsSection title="New legal hold" description="Name the matter, then add the recordings, people or teams it covers.">
        <div className="admin-inline-form">
          <Field label="Name" placeholder="Smith v. Acme" value={name} onChange={(e) => setName(e.target.value)} />
          <Field label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button onClick={() => void onCreate()} loading={creating} disabled={!name.trim()}>
            Create hold
          </Button>
        </div>
        {createError ? <Notice tone="error">{createError}</Notice> : null}
      </SettingsSection>

      {loading ? (
        <div className="admin-row-sub">Loading…</div>
      ) : (
        <>
          <div className="gk-overline">Active ({active.length})</div>
          {active.length === 0 ? (
            <div className="admin-row-sub">No active holds.</div>
          ) : (
            <div className="admin-stack">{active.map(renderRow)}</div>
          )}

          {released.length > 0 ? (
            <>
              <div className="gk-overline">Released ({released.length})</div>
              <div className="admin-stack">{released.map(renderRow)}</div>
            </>
          ) : null}
        </>
      )}
    </>
  );
}
